import { useQuery } from '@apollo/react-hooks';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import type {
	getNumberCurrencyFormattingChangeboardingCriteria as Response,
	getNumberCurrencyFormattingChangeboardingCriteriaVariables as Variables,
} from './__generated_apollo__/getNumberCurrencyFormattingChangeboardingCriteria.ts';
import { GET_NUMBER_CURRENCY_FORMATTING_CHANGEBOARDING_CRITIERA_QUERY } from './gql.tsx';

const getURLPrefix = (projectType?: string | null) => {
	if (projectType === 'SOFTWARE') return 'software';
	if (projectType === 'BUSINESS') return 'core';
	return null;
};

export const useFetchCriteria = (projectId: string, projectKey: string, enabled?: boolean) => {
	const cloudId = useCloudId();
	const projectAri = JiraProjectAri.create({
		siteId: cloudId,
		projectId,
	});

	const { data } = useQuery<Response, Variables>(
		GET_NUMBER_CURRENCY_FORMATTING_CHANGEBOARDING_CRITIERA_QUERY,
		{
			variables: {
				projectAri: projectAri.toString(),
				projectKey,
				first: 100,
			},
			fetchPolicy: 'network-only',
			skip: !enabled,
		},
	);

	if (!data) return undefined;

	const isProjectAdmin = data.jira?.jiraProject?.administerProject?.canPerform ?? false;

	// Check if there already exists a JiraNumberField with a truthy formatConfig. This means an admin has already interacted with the new feature.
	const hasExistingNumberFieldWithFormatStyle =
		data.jiraBusinessAvailableFields?.edges?.some((edge) => {
			return edge?.node?.fields?.edges?.some((fieldEdge) => {
				const field = fieldEdge?.node;
				return field?.__typename === 'JiraNumberField' && field?.formatConfig?.formatStyle != null;
			});
		}) ?? false;

	const projectType = getURLPrefix(data.jira?.jiraProject?.projectType);
	const isTMP = Boolean(data.jira?.jiraProject?.tmpBoardId);

	return {
		isProjectAdmin,
		hasExistingNumberFieldWithFormatStyle,
		projectType,
		isTMP,
	};
};
